import React, { useState, useEffect, useRef, useMemo } from 'react';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  Typography,
  Box,
  Alert,
  Chip,
  Autocomplete,
  FormControlLabel,
  RadioGroup,
  Radio,
  IconButton,
  Tooltip,
  Divider,
} from '@mui/material';
import { LocalizationProvider, DateTimePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import 'dayjs/locale/ko';
import { MeetingRoom, User, Booking } from '../../types/models';
import {
  canBookRoomOn,
  formatClosureBadge,
  formatClosureDate,
  hasClosureDate,
  isRoomClosed
} from '../../utils/roomAvailability';
import RoomDetailModal from '../room/RoomDetailModal';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ClearIcon from '@mui/icons-material/Clear';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom';
import {
  trackBookingCreation,
  trackBookingUpdate,
  trackBookingConflict,
} from '../../services/analytics';
import { getAllUsers } from '../../services/userService';
import {
  checkRoomConflict,
  getRecurringConflicts,
  getBookings,
  createRecurringBookingWithConflictFilter,
  endMeetingNow
} from '../../services/bookingService';
import { supabase } from '../../services/supabase';
import { Dialog as MuiDialog } from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import disabledTheme from '../../styles/theme';

// dayjs 한국어 설정
dayjs.locale('ko');

// iOS 스타일의 스위치 컴포넌트 정의

// 반복 예약 삭제 유형 정의
export type RecurrenceDeleteType = 'THIS_EVENT' | 'THIS_AND_FOLLOWING' | 'ALL_EVENTS';

export interface BookingModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (booking: {
    id?: string; // ID 추가
    title: string;
    startTime: Date;
    endTime: Date;
    roomName: string;
    description?: string;
    attendee_uids: string[]; // attendees에서 attendee_uids로 변경
    color: string;
    is_reminder_enabled: boolean;
    reminder_minutes: number; // 추가: 리마인더 알림 시간
    recurrence_type?: string;
    recurrence_end_date?: Date | null;
  }) => void;
  onDelete?: (bookingId: string, recurrenceDeleteType?: RecurrenceDeleteType) => void;
  selectedDate?: Date;
  selectedRoom?: MeetingRoom | null;
  editingBooking?: {
    id: string;
    title: string;
    start: Date;
    end: Date;
    backgroundColor?: string;
    borderColor?: string;
    extendedProps: {
      roomName: string;
      description?: string;
      attendee_uids: string[]; // attendees에서 attendee_uids로 변경
      userId?: string;
      userName?: string;
      is_reminder_enabled?: boolean;
      reminder_minutes: number;
      recurrence_type?: string;
      recurrence_end_date?: Date | null;
    };
  };
  rooms: Array<MeetingRoom>;
  currentUser: User | null;
  setSnackbar: (snackbar: { open: boolean; message: string; severity: 'success' | 'error' | 'info' }) => void;
  onRefreshBookings?: () => void;
}

const BookingModal: React.FC<BookingModalProps> = ({
  open,
  onClose,
  onSave,
  onDelete,
  selectedRoom,
  selectedDate,
  editingBooking,
  rooms,
  currentUser,
  setSnackbar,
  onRefreshBookings
}) => {
  // 반복 예약 삭제 관련 상태
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  const [recurrenceDeleteType, setRecurrenceDeleteType] = useState<RecurrenceDeleteType>('THIS_EVENT');

  // 겹치는 반복 예약 안내 모달 상태
  const [showConflictDialog, setShowConflictDialog] = useState(false);
  const [pendingBookingData, setPendingBookingData] = useState<any>(null);
  const [pendingConflicts, setPendingConflicts] = useState<any[]>([]); // 겹치는 예약 정보를 저장할 상태

  // 저장 요청이 진행 중인지 여부. 더블클릭으로 같은 예약이 두 번 들어가는 것을 막는다.
  //
  // 저장 처리에는 기존 예약 목록을 불러오는 await가 들어 있어서, 그 사이에는
  // 버튼이 아직 눌리는 상태로 남는다. 두 번째 클릭이 그 틈에 들어오면 두 호출 모두
  // "아직 DB에 없는" 목록을 보고 중복 검사를 통과해 예약이 두 건 생긴다.
  //
  // state가 아니라 ref로 두는 이유: setState는 비동기라 빠르게 두 번 눌리면
  // 두 호출이 모두 갱신 전 값을 읽고 그대로 통과해버린다. ref는 즉시 반영된다.
  // (state 쪽은 버튼을 비활성화해 보여주기 위한 용도)
  const isSubmittingRef = useRef(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  /**
   * 저장 작업을 시작한다.
   * 이미 진행 중이면 false를 돌려주며, 호출한 쪽은 그대로 빠져나가야 한다.
   * true를 받았다면 끝날 때 반드시 finishSubmit()을 불러야 한다.
   */
  const beginSubmit = (): boolean => {
    if (isSubmittingRef.current) return false;
    isSubmittingRef.current = true;
    setIsSubmitting(true);
    return true;
  };

  const finishSubmit = () => {
    isSubmittingRef.current = false;
    setIsSubmitting(false);
  };
  // showAllConflicts, setShowAllConflicts 등 불필요한 상태/코드 제거


  const [formData, setFormData] = useState<{
    title: string;
    description: string;
    startTime: dayjs.Dayjs | null;
    endTime: dayjs.Dayjs | null;
    roomName: string;
    color: string;
    is_reminder_enabled: boolean;
    reminder_minutes: number; // 추가: 리마인더 알림 시간
    recurrence_type: string;
    recurrence_end_date: dayjs.Dayjs | null;
    show_recurrence_notice: boolean;
  }>({
    title: '',
    description: '',
    startTime: null, // 현재 시간으로 초기화
    endTime: null, // 현재 시간 + 1시간으로 초기화
    roomName: '', //초기값
    color: '', //초기값
    is_reminder_enabled: true,
    reminder_minutes: 30, // 기본값: 30분 전
    recurrence_type: '반복 안함',
    recurrence_end_date: dayjs().add(3, 'month'), // 기본값으로 3개월 후로 설정
    show_recurrence_notice: false,
  });
  
  // 모든 사용자 목록 상태
  const [allUsers, setAllUsers] = useState<User[]>([]);
  // 선택된 참석자 상태 (User 객체 배열)
  const [selectedAttendees, setSelectedAttendees] = useState<User[]>([]);
  // 사용자 정보 로딩 상태
  const [isLoadingUsers, setIsLoadingUsers] = useState<boolean>(false);

  // 유효성 에러 상태 추가
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  // 아래 오류들은 해당 입력칸 옆에 직접 표시되므로 따로 모아 보여주지 않는다.
  const INLINE_ERROR_MESSAGES = [
    '회의실을 선택하세요.',
    '회의 제목을 입력하세요.',
    '시작 시간을 선택하세요.',
    '종료 시간을 선택하세요.',
    '시작 일자와 종료 일자는 같아야 합니다.',
    '시작 시간은 현재 시각 이후여야 합니다.',
    '종료 시간은 시작 시간보다 이후여야 합니다.',
    '예약 시간은 최소 10분 이상이어야 합니다.'
  ];
  const generalErrors = validationErrors.filter(message => !INLINE_ERROR_MESSAGES.includes(message));

  const getUserDisplayName = (user: User) => user.name || user.display_name || user.email || '이름 없음';
  const getUserTeam = (user: User) => user.team || '';
  const getUserPosition = (user: User) => user.position || '';
  const getUserLabel = (user: User) => {
    const name = getUserDisplayName(user);
    const position = getUserPosition(user);
    if (!position) return name;
    const team = getUserTeam(user);
    return team ? `${name} ${position}(${team})` : `${name} ${position}`;
  };

  // 현재 선택된 회의실 정보 찾기
  const currentRoom = rooms.find(room => room.name === formData.roomName) || selectedRoom || null;

  // 예약 기간이 끝난 회의실은 목록에서 뺀다.
  // 다만 그 회의실로 잡아 둔 지난 예약을 열어 볼 때는 선택값이 비면 안 되므로 남겨 둔다.
  const selectableRooms = useMemo(() => {
    const openRooms = rooms.filter(room => !isRoomClosed(room));
    if (currentRoom && !openRooms.some(room => room.id === currentRoom.id)) {
      return [currentRoom, ...openRooms];
    }
    return openRooms;
  }, [rooms, currentRoom]);

  const [isRoomDetailModalOpen, setIsRoomDetailModalOpen] = useState<boolean>(false);
  const [startTimePickerOpen, setStartTimePickerOpen] = useState<boolean>(false);
  const [endTimePickerOpen, setEndTimePickerOpen] = useState<boolean>(false);

  // 회의실 정보 로깅
  useEffect(() => {
    console.log('BookingModal - 받은 회의실 목록:', rooms);
    console.log('BookingModal - 현재 선택된 회의실:', currentRoom);
  }, [rooms, formData.roomName, currentRoom]);
  
  // 모든 사용자 목록 불러오기 (세션이 있을 때만 호출해 콘솔 에러 방지)
  useEffect(() => {
    let cancelled = false;

    const fetchAllUsers = async () => {
      try {
        setIsLoadingUsers(true);
        // Vercel 프리뷰 등 새 오리진에서는 세션이 없을 수 있으므로 먼저 확인
        const { data } = await supabase.auth.getSession();
        if (!data.session) {
          console.warn('세션이 없어 참석자 목록 조회를 건너뜁니다.');
          return;
        }
        const usersData = await getAllUsers();
        if (!cancelled) {
          setAllUsers(usersData);
          console.log('사용자 목록 로드 완료:', usersData.length);
        }
      } catch (error) {
        console.error('사용자 목록 로드 실패:', error);
      } finally {
        if (!cancelled) {
          setIsLoadingUsers(false);
        }
      }
    };

    // 사용자 목록은 모달을 열 때 한 번만 불러온다 (캐시가 있으면 캐시 사용).
    if (open) {
      fetchAllUsers();
    }

    return () => {
      cancelled = true;
    };
  }, [open]);

  useEffect(() => {
    if (editingBooking) {
      // 편집 중인 예약에서 색상 가져오기
      const bookingColor = editingBooking.backgroundColor || (selectedRoom?.color || '');
      
      // DB 값을 UI 표시 값으로 변환
      let uiRecurrenceType = '반복 안함';
      if (editingBooking.extendedProps.recurrence_type === 'WEEKLY') {
        uiRecurrenceType = '매주 반복';
      } else if (editingBooking.extendedProps.recurrence_type === 'BI_WEEKLY') {
        uiRecurrenceType = '격주 반복';
      } else if (editingBooking.extendedProps.recurrence_type === 'MONTHLY') {
        uiRecurrenceType = '매월 반복';
      }
      
      setFormData({
        title: editingBooking.title,
        description: editingBooking.extendedProps.description || '',
        startTime: dayjs(editingBooking.start),
        endTime: dayjs(editingBooking.end),
        roomName: editingBooking.extendedProps.roomName,
        color: selectedRoom?.color || bookingColor || '',
        is_reminder_enabled: editingBooking.extendedProps.is_reminder_enabled !== undefined ? editingBooking.extendedProps.is_reminder_enabled : true,
        reminder_minutes: editingBooking.extendedProps.reminder_minutes || 30,
        recurrence_type: uiRecurrenceType,
        recurrence_end_date: editingBooking.extendedProps.recurrence_end_date ? dayjs(editingBooking.extendedProps.recurrence_end_date) : dayjs().add(3, 'month'),
        show_recurrence_notice: editingBooking.extendedProps.recurrence_type && editingBooking.extendedProps.recurrence_type !== 'NONE' ? true : false,
      });
      
    // } else if (selectedRoom) {
    // BookingModal.tsx 파일 내 `useEffect` 훅 내부의 `else` 블록 (새 예약 생성 시)
// 이 부분이 가장 최근에 제안했던 수정 내용입니다.
    } else if (open) {
        const now = dayjs();
        let initialStartTime: dayjs.Dayjs;

        let baseDate = selectedDate ? dayjs(selectedDate) : now;

        const currentMinute = now.minute();
        // 10분 단위로 반올림 (변경 전: 30분 단위)
        const roundedNowMinute = Math.ceil(currentMinute / 10) * 10;
        // const roundedNowMinute = Math.ceil(currentMinute / 30) * 30;
        let roundedNow = now.minute(roundedNowMinute).second(0).millisecond(0);

        if (roundedNowMinute === 60) {
            roundedNow = roundedNow.add(1, 'hour').minute(0);
        }
        
        // ✨ 핵심 로직: selectedDate가 오늘보다 이전 날짜인 경우, 무조건 오늘의 현재 시각 이후로 보정 ✨
        if (baseDate.isBefore(now, 'day')) {
          initialStartTime = roundedNow;
        } else {
          initialStartTime = baseDate.hour(roundedNow.hour()).minute(roundedNow.minute()).second(0).millisecond(0);
          if (initialStartTime.isBefore(roundedNow)) {
            initialStartTime = roundedNow;
          }
        }

        setFormData(prev => ({
          ...prev,
          title: '',
          description: '',
          startTime: initialStartTime, // 여기가 보정된 시간으로 설정됩니다.
          // 초기 종료 시간: 시작 시간 + 10분 (변경 전: 30분)
          endTime: initialStartTime.add(10, 'minute'),
          // endTime: initialStartTime.add(30, 'minute'),
          roomName: selectedRoom ? selectedRoom.name : '',
          color: selectedRoom ? selectedRoom.color || '' : '',
          is_reminder_enabled: true,
          recurrence_type: '반복 안함',
          recurrence_end_date: dayjs().add(3, 'month'),
          show_recurrence_notice: false,
        }));
        setSelectedAttendees([]);
    }
  }, [editingBooking, selectedDate, open, selectedRoom]);

  // 고를 수 있는 회의실이 하나뿐이면 굳이 고르게 하지 않는다.
  //
  // 위의 폼 초기화 effect 가 회의실을 비우므로 반드시 그 뒤에 와야 한다.
  // 앞에 두면 초기화가 덮어써서 아무 효과가 없다.
  useEffect(() => {
    if (!open || editingBooking) return;
    if (formData.roomName) return;
    if (selectableRooms.length !== 1) return;

    const onlyRoom = selectableRooms[0];
    setFormData(prev => ({ ...prev, roomName: onlyRoom.name, color: onlyRoom.color || '' }));
  }, [open, editingBooking, formData.roomName, selectableRooms]);

  useEffect(() => {
    if (!open || allUsers.length === 0) {
      return;
    }

    if (editingBooking && Array.isArray(editingBooking.extendedProps.attendee_uids)) {
      const attendeeUsers = editingBooking.extendedProps.attendee_uids
        .map(uid => allUsers.find(user => user.id === uid))
        .filter((user): user is User => user !== undefined);
      setSelectedAttendees(attendeeUsers);
      return;
    }

    setSelectedAttendees(prev =>
      prev
        .map(attendee => allUsers.find(user => user.id === attendee.id) || attendee)
    );
  }, [allUsers, editingBooking, open]);

  // 시작시간 변경 시 종료시간 자동 조정
  const handleStartTimeChange = (date: dayjs.Dayjs | null) => {
    setFormData(prev => {
      if (!date) return { ...prev, startTime: null };
      return { ...prev, startTime: date, endTime: date.add(1, 'hour') };
    });
  };

  // 종료시간 변경 핸들러(기존과 동일)
  const handleEndTimeChange = (date: dayjs.Dayjs | null) => {
    setFormData(prev => ({ ...prev, endTime: date }));
  };


  const validate = () => {
    const errors: string[] = [];

    if (!formData.roomName) errors.push('회의실을 선택하세요.');
    if (!formData.title || formData.title.trim() === '') errors.push('회의 제목을 입력하세요.');
    if (!formData.startTime) errors.push('시작 시간을 선택하세요.');
    if (!formData.endTime) errors.push('종료 시간을 선택하세요.');

    // 1. 시작일과 종료일이 같은지 확인
    if (formData.startTime && formData.endTime && !formData.startTime.isSame(formData.endTime, 'date')) {
      errors.push('시작 일자와 종료 일자는 같아야 합니다.');
    }

    // 2. 시작 시간이 현재 시각 이후인지 확인
    // 현재 시각: 2025년 7월 22일 15:26
    if (formData.startTime && formData.startTime.isBefore(dayjs(), 'minute')) {
      errors.push('시작 시간은 현재 시각 이후여야 합니다.');
    }

    // 3. 종료 시간이 시작 시간보다 이후여야 합니다. 👈 이 로직 추가!
    if (formData.startTime && formData.endTime && !formData.endTime.isAfter(formData.startTime)) {
      errors.push('종료 시간은 시작 시간보다 이후여야 합니다.');
  }

    // 4. 예약 시간은 최소 10분 이상이어야 합니다.
    // diff 메서드를 사용하여 분 단위 차이 계산
    // (변경 전: 30분 → 변경 후: 10분)
    if (formData.startTime && formData.endTime && formData.endTime.diff(formData.startTime, 'minute') < 10) {
        errors.push('예약 시간은 최소 10분 이상이어야 합니다.');
    }

    // 5. 회의실 사용 종료일 확인 (rooms.available_until)
    //    사용이 끝나는 회의실은 그 날짜까지만 예약할 수 있다.
    if (currentRoom && hasClosureDate(currentRoom)) {
      const closureDate = formatClosureDate(currentRoom);

      if (formData.startTime && !canBookRoomOn(currentRoom, formData.startTime)) {
        errors.push(`${currentRoom.name}은(는) ${closureDate}까지만 예약할 수 있습니다.`);
      }

      // 반복 예약은 마지막 회차까지 모두 범위 안이어야 한다.
      // 일부만 만들고 나머지를 조용히 빼면 예약한 사람이 알아채지 못한다.
      if (
        formData.recurrence_type !== '반복 안함' &&
        formData.recurrence_end_date &&
        !canBookRoomOn(currentRoom, formData.recurrence_end_date)
      ) {
        errors.push(`${currentRoom.name}은(는) ${closureDate}까지만 예약할 수 있어 반복 종료일을 그 이전으로 맞춰야 합니다.`);
      }
    }
    // if (formData.startTime && formData.endTime && formData.endTime.diff(formData.startTime, 'minute') < 30) {
    //     errors.push('예약 시간은 최소 30분 이상이어야 합니다.');
    // }

    return errors;
  };

  // 반복 인스턴스 전체 계산 유틸 함수
  function calculateAllInstances(start: Date, end: Date, recurrence_type: string, recurrence_end_date: Date | null) {
    const durationMs = end.getTime() - start.getTime();
    const endDate = recurrence_end_date ? new Date(recurrence_end_date) : new Date(start);
    endDate.setHours(23, 59, 59);
    let currentStart = new Date(start);
    let currentEnd = new Date(end);
    const allInstances = [{ start: new Date(currentStart), end: new Date(currentEnd) }];
    if (recurrence_type === 'WEEKLY' || recurrence_type === 'BI_WEEKLY' || recurrence_type === 'MONTHLY') {
      if (recurrence_type === 'WEEKLY') {
        currentStart.setDate(currentStart.getDate() + 7);
        currentEnd = new Date(currentStart.getTime() + durationMs);
      } else if (recurrence_type === 'BI_WEEKLY') {
        currentStart.setDate(currentStart.getDate() + 14);
        currentEnd = new Date(currentStart.getTime() + durationMs);
      } else if (recurrence_type === 'MONTHLY') {
        currentStart.setMonth(currentStart.getMonth() + 1);
        currentEnd = new Date(currentStart.getTime() + durationMs);
      }
      while (currentStart <= endDate) {
        allInstances.push({ start: new Date(currentStart), end: new Date(currentEnd) });
        if (recurrence_type === 'WEEKLY') {
          currentStart.setDate(currentStart.getDate() + 7);
          currentEnd = new Date(currentStart.getTime() + durationMs);
        } else if (recurrence_type === 'BI_WEEKLY') {
          currentStart.setDate(currentStart.getDate() + 14);
          currentEnd = new Date(currentStart.getTime() + durationMs);
        } else if (recurrence_type === 'MONTHLY') {
          currentStart.setMonth(currentStart.getMonth() + 1);
          currentEnd = new Date(currentStart.getTime() + durationMs);
        } else {
          break;
        }
      }
    }
    return allInstances;
  }

  const submitBooking = async () => {
    const errors = validate();
    console.log('Validation Errors on Submit:', errors); // ⬅️ 이 부분을 추가

    setValidationErrors(errors);
    if (errors.length > 0) return;
    if (!formData.startTime || !formData.endTime || !currentRoom) return;

    // 기존 예약 목록 불러오기
    let existingBookings: any[] = [];
    try {
      existingBookings = await getBookings();
    } catch (e) {}

    let dbRecurrenceType = 'NONE';
    if (formData.recurrence_type === '매주 반복') {
      dbRecurrenceType = 'WEEKLY';
    } else if (formData.recurrence_type === '격주 반복') {
      dbRecurrenceType = 'BI_WEEKLY';
    } else if (formData.recurrence_type === '매월 반복') {
      dbRecurrenceType = 'MONTHLY';
    }
    const roomColor = currentRoom?.color || '';
    const bookingData = {
      title: formData.title,
      startTime: formData.startTime?.toDate() || new Date(),
      endTime: formData.endTime?.toDate() || new Date(),
      roomName: formData.roomName,
      roomId: currentRoom.id, // 반드시 포함
      description: formData.description,
      attendee_uids: selectedAttendees.map(user => user.id),
      color: roomColor,
      is_reminder_enabled: formData.is_reminder_enabled,
      reminder_minutes: formData.reminder_minutes,
      recurrence_type: dbRecurrenceType,
      recurrence_end_date: formData.recurrence_end_date?.toDate() || null,
    };

    const roomId = bookingData.roomId;
    const start = bookingData.startTime;
    const end = bookingData.endTime;
    const excludeId = editingBooking?.id;

    // 반복 예약이 아닌 경우: 시간/장소 겹침 체크
    if (!editingBooking && dbRecurrenceType === 'NONE') {
      if (checkRoomConflict(roomId, start, end, existingBookings)) {
        setSnackbar({
          open: true,
          message: '이미 해당 시간에 같은 회의실에 예약이 존재합니다.',
          severity: 'error'
        });
        return;
      }
      onSave(bookingData);
      // Analytics: Track booking creation
      trackBookingCreation({
        ...bookingData,
        id: '', // Use empty string if no ID yet
        userId: currentUser?.id || '',
      });
      handleClose();
      return;
    }
    // 예약 수정 시: 자기 자신 제외하고 시간/장소 겹침 체크
    if (editingBooking) {
      if (checkRoomConflict(roomId, start, end, existingBookings, excludeId)) {
        setSnackbar({
          open: true,
          message: '이미 해당 시간에 같은 회의실에 예약이 존재합니다.',
          severity: 'error'
        });
        return;
      }
      onSave({ ...bookingData, id: editingBooking.id });
      // Analytics: Track booking update
      trackBookingUpdate(
        {
          ...bookingData,
          id: editingBooking.id,
          userId: currentUser?.id || '',
        },
        {
          id: editingBooking.id,
          title: editingBooking.title,
          startTime: editingBooking.start,
          endTime: editingBooking.end,
          roomName: editingBooking.extendedProps.roomName,
          roomId: currentRoom?.id || '',
          description: editingBooking.extendedProps.description || '',
          attendee_uids: editingBooking.extendedProps.attendee_uids || [],
          color: editingBooking.backgroundColor || '',
          is_reminder_enabled: editingBooking.extendedProps.is_reminder_enabled ?? true,
          reminder_minutes: editingBooking.extendedProps.reminder_minutes || 30,
          recurrence_type: editingBooking.extendedProps.recurrence_type || '',
          recurrence_end_date: editingBooking.extendedProps.recurrence_end_date || null,
          userId: editingBooking.extendedProps.userId || '',
        }
      );
      handleClose();
      return;
    }
    // 반복 예약 추가 시: 반복 인스턴스 전체에 대해 중복 체크
    if (!editingBooking && dbRecurrenceType !== 'NONE') {
      const allInstances = calculateAllInstances(start, end, dbRecurrenceType, bookingData.recurrence_end_date);
      const conflicts = allInstances
        .map(inst => getRecurringConflicts(roomId, inst.start, inst.end, existingBookings))
        .flat();
      if (conflicts.length > 0) {
        setPendingBookingData(bookingData);
        setPendingConflicts(conflicts);
        // Analytics: Track booking conflict for recurring
        if (currentUser) {
          trackBookingConflict(
            {
              ...bookingData,
              id: '',
              userId: currentUser.id,
              attendee_uids: selectedAttendees.map(user => user.id),
              startTime: formData.startTime?.toDate() || new Date(),
              endTime: formData.endTime?.toDate() || new Date(),
              roomId: currentRoom?.id || '',
            },
            conflicts
          );
        }
        setShowConflictDialog(true);
        return;
      }
      // 겹침이 없으면 바로 반복 예약 생성
      const userId = currentUser?.id || '';
      const result = await createRecurringBookingWithConflictFilter({ ...bookingData, userId });
      setSnackbar({
        open: true,
        message: `${result.created.length}개의 반복 예약이 생성되었습니다.`,
        severity: 'success'
      });
      // Analytics: Track booking creation for each created
      if (result.created && Array.isArray(result.created)) {
        result.created.forEach((booking: Booking) => {
          trackBookingCreation({
            ...booking,
            userId: currentUser?.id || '',
            roomId: booking.roomId || currentRoom?.id || '',
          });
        });
      }
      if (onRefreshBookings) onRefreshBookings();
      handleClose();
      return;
    }
  };

  // 예약/수정 버튼 핸들러.
  // 실제 처리는 submitBooking이 하고, 여기서는 중복 제출만 막는다.
  const handleSubmit = async () => {
    if (!beginSubmit()) return;
    try {
      await submitBooking();
    } finally {
      finishSubmit();
    }
  };

  // 겹침 안내 모달에서 확인 시 예약 진행
  const handleConfirmConflict = async () => {
    // 이 버튼도 생성 요청을 보내므로 똑같이 중복 제출을 막는다
    if (!beginSubmit()) return;
    try {
      await confirmConflictBooking();
    } finally {
      finishSubmit();
    }
  };

  const confirmConflictBooking = async () => {
    if (!pendingBookingData) {
      setShowConflictDialog(false);
      handleClose();
      return;
    }
    const userId = currentUser?.id || '';
    const result = await createRecurringBookingWithConflictFilter({ ...pendingBookingData, userId });
    if (result.created.length === 0) {
      setSnackbar({
        open: true,
        message: '모든 반복 일정이 중복되어 예약이 생성되지 않았습니다.',
        severity: 'error'
      });
    } else {
      setSnackbar({
        open: true,
        message: `${result.created.length}개의 반복 예약이 생성되었습니다.`,
        severity: 'success'
      });
      if (onRefreshBookings) onRefreshBookings();
    }
    setPendingBookingData(null);
    setPendingConflicts([]);
    setShowConflictDialog(false);
    handleClose();
  };
  const handleCancelConflict = () => {
    setPendingBookingData(null);
    setPendingConflicts([]); // 겹치는 예약 정보 초기화
    setShowConflictDialog(false);
  };
  
  // 회의 종료 핸들러 (진행 중인 회의를 현재 시각 기준 10분 올림으로 종료)
  const handleEndMeeting = async () => {
    if (!editingBooking) return;
    try {
      await endMeetingNow(editingBooking.id);
      handleClose();
      if (onRefreshBookings) onRefreshBookings();
    } catch (e) {
      console.error('회의 종료 처리 실패:', e);
    }
  };

  // 삭제 버튼 클릭 핸들러
  const handleDeleteClick = () => {
    // 반복 예약인 경우 삭제 옵션 다이얼로그 표시
    if (editingBooking && editingBooking.extendedProps.recurrence_type && 
        editingBooking.extendedProps.recurrence_type !== 'NONE') {
      setDeleteDialogOpen(true);
    } else if (editingBooking && onDelete) {
      // 일반 예약인 경우 바로 삭제
      onDelete(editingBooking.id);
      handleClose();
    }
  };
  
  // 최종 삭제 처리 핸들러
  const handleDelete = () => {
    if (editingBooking && onDelete) {
      onDelete(editingBooking.id, recurrenceDeleteType);
      setDeleteDialogOpen(false);
      handleClose();
    }
  };
  
  // 삭제 다이얼로그 닫기
  const handleDeleteDialogClose = () => {
    setDeleteDialogOpen(false);
  };

  const handleClose = () => {
    setFormData({
      title: '',
      description: '',
      startTime: null,
      endTime: null,
      roomName: '',
      color: '',
      is_reminder_enabled: true,
      reminder_minutes: 30,
      recurrence_type: '반복 안함',
      recurrence_end_date: dayjs().add(3, 'month'),
      show_recurrence_notice: false,
    });
    setSelectedAttendees([]);
    setIsRoomDetailModalOpen(false);
    onClose();
  };

  // 참석자 제거 핸들러
  const handleRemoveAttendee = (userToRemove: User) => {
    setSelectedAttendees(prev => prev.filter(user => user.id !== userToRemove.id));
  };

  // 현재 사용자가 로그인 상태인지 확인
  const isLoggedIn = currentUser !== null;

  // 현재 사용자가 예약 소유자인지 확인
  // 예약자 ID가 없거나 비어있는 경우에는 수정/삭제 불가
  const isOwner = isLoggedIn && editingBooking
    ? Boolean(editingBooking.extendedProps.userId) &&
      editingBooking.extendedProps.userId === currentUser?.id
    : isLoggedIn;

  // 지난 예약 여부 (종료 시간이 현재보다 이전)
  const isPastBooking = editingBooking
    ? dayjs(editingBooking.end).isBefore(dayjs())
    : false;

  // 진행 중인 예약 여부 (시작됐지만 아직 종료 안 됨)
  const isInProgress = editingBooking
    ? dayjs(editingBooking.start).isBefore(dayjs()) && dayjs(editingBooking.end).isAfter(dayjs())
    : false;

  // 회의 종료 버튼 노출 여부 (종료 누를 경우 실제로 시간이 줄어드는 경우만)
  const now = dayjs();
  const remainder = now.minute() % 10;
  const roundedEnd = remainder === 0 ? now : now.add(10 - remainder, 'minute').startOf('minute');
  const canEndEarly = isInProgress && editingBooking
    ? roundedEnd.isBefore(dayjs(editingBooking.end))
    : false;

  // 예약 정보 조회 모드인지 확인 (내 예약이 아니거나 로그인하지 않은 경우, 또는 지난/진행 중인 예약)
  const isViewMode = (editingBooking && !isOwner) || !isLoggedIn || isPastBooking || isInProgress;

  // 모달이 열릴 때마다 에러 초기화
  useEffect(() => {
    if (open) {
      setValidationErrors([]);
    } else {
      setIsRoomDetailModalOpen(false);
    }
  }, [open]);

  return (
    <ThemeProvider theme={isViewMode ? disabledTheme : createTheme({})}>
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="ko">
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth={isViewMode ? false : 'md'}
        fullWidth={!isViewMode}
        PaperProps={isViewMode ? { sx: { minWidth: { xs: '90vw', sm: 400 }, maxWidth: { xs: '95vw', sm: 520 } } } : undefined}
      >
        <DialogTitle sx={{ fontSize: 14, fontWeight: 500, color: 'text.secondary', pb: 0.5, px: { xs: 2.5, sm: 3 }, pt: { xs: 2, sm: 2 } }}>
          {editingBooking ? (isViewMode ? '예약 정보' : '예약 수정') : '새 예약 생성'}
        </DialogTitle>
        <DialogContent sx={{ px: { xs: 2.5, sm: 3 }, pb: { xs: 2, sm: 2 } }}>
          {isViewMode ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', pt: 0.5, pb: 1 }}>

              {/* 제목 + 예약자 */}
              <Box sx={{ mb: 3 }}>
                <Typography
                  sx={{
                    fontSize: 22,
                    fontWeight: 700,
                    lineHeight: 1.3,
                    wordBreak: 'break-word',
                    color: 'text.primary',
                  }}
                >
                  {formData.title || '(제목 없음)'}
                </Typography>
                {editingBooking?.extendedProps?.userName && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                    예약자 · {editingBooking.extendedProps.userName}
                  </Typography>
                )}
              </Box>

              <Divider sx={{ mb: 2.5 }} />

              {/* 시간 + 회의실 + 예약자 */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 2.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                  <AccessTimeIcon sx={{ fontSize: 18, color: 'text.disabled', mt: 0.2, flexShrink: 0 }} />
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {formData.startTime?.format('YYYY년 M월 D일 (dd)')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                      {formData.startTime && formData.endTime
                        ? (() => { const mins = Math.max(0, formData.endTime.diff(formData.startTime, 'minute')); const h = Math.floor(mins / 60); const m = mins % 60; const dur = h > 0 && m > 0 ? `${h}시간 ${m}분` : h > 0 ? `${h}시간` : `${m}분`; return `${formData.startTime.format('A h:mm')} ~ ${formData.endTime.format('A h:mm')} · ${dur}`; })()
                        : '시간 정보 없음'}
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <MeetingRoomIcon sx={{ fontSize: 18, color: 'text.disabled', flexShrink: 0 }} />
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {formData.roomName || currentRoom?.name || '회의실 정보 없음'}
                  </Typography>
                  {currentRoom && (
                    <Tooltip title="회의실 상세">
                      <IconButton size="small" onClick={() => setIsRoomDetailModalOpen(true)} sx={{ p: 0.25 }}>
                        <InfoOutlinedIcon sx={{ fontSize: 14, color: 'primary.main' }} />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>

              </Box>

              {/* 참석자 */}
              {selectedAttendees.length > 0 && (
                <>
                  <Divider sx={{ mb: 2 }} />
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 1 }}>
                      참석자
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                      {selectedAttendees.map((u) => (
                        <Chip key={u.id} label={getUserLabel(u)} size="small" />
                      ))}
                    </Box>
                  </Box>
                </>
              )}

              {/* 내용 */}
              {formData.description?.trim() && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 0.75 }}>
                      내용
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
                      {formData.description}
                    </Typography>
                  </Box>
                </>
              )}
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 0.5 }}>

              {/* ── 카드 1: 일정 ── */}
              <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 1.5, display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>일정</Typography>

                {/* 날짜 / 시간 */}
                <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
                  <Box sx={{ flex: 1 }}>
                    <DateTimePicker
                      label="시작 시간"
                      value={formData.startTime}
                      minDateTime={dayjs()}
                      open={startTimePickerOpen}
                      onOpen={() => setStartTimePickerOpen(true)}
                      onClose={() => setStartTimePickerOpen(false)}
                      onChange={(date) => {
                        handleStartTimeChange(date);
                        setValidationErrors(prev => prev.filter(
                          err => !(
                            err === '시작 시간은 현재 시각 이후여야 합니다.' ||
                            err === '시작 시간을 선택하세요.' ||
                            err === '시작 일자와 종료 일자는 같아야 합니다.'
                          )
                        ));
                      }}
                      onAccept={() => { setStartTimePickerOpen(false); setEndTimePickerOpen(true); }}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          size: "small",
                          InputProps: { sx: { fontSize: 13 } },
                          onClick: () => { if (!isViewMode) setStartTimePickerOpen(true); },
                          error: (formData.startTime && validationErrors.includes('시작 시간은 현재 시각 이후여야 합니다.')) ||
                                 validationErrors.includes('시작 시간을 선택하세요.') ||
                                 validationErrors.includes('시작 일자와 종료 일자는 같아야 합니다.') ||
                                 validationErrors.includes('종료 시간은 시작 시간보다 이후여야 합니다.') ||
                                 validationErrors.includes('예약 시간은 최소 10분 이상이어야 합니다.'),
                        },
                      }}
                      timeSteps={{ minutes: 10 }}
                      readOnly={isViewMode}
                    />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <DateTimePicker
                      label="종료 시간"
                      value={formData.endTime}
                      minDateTime={dayjs()}
                      open={endTimePickerOpen}
                      onOpen={() => setEndTimePickerOpen(true)}
                      onClose={() => setEndTimePickerOpen(false)}
                      onChange={(date) => {
                        handleEndTimeChange(date);
                        setValidationErrors(prev => prev.filter(
                          err => !(
                            err === '종료 시간을 선택하세요.' ||
                            err === '시작 일자와 종료 일자는 같아야 합니다.'
                          )
                        ));
                      }}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          size: "small",
                          InputProps: { sx: { fontSize: 13 } },
                          onClick: () => { if (!isViewMode) setEndTimePickerOpen(true); },
                          error: (formData.endTime && validationErrors.includes('종료 시간은 시작 시간보다 이후여야 합니다.')) ||
                                 (formData.endTime && validationErrors.includes('예약 시간은 최소 10분 이상이어야 합니다.')) ||
                                 validationErrors.includes('종료 시간을 선택하세요.') ||
                                 validationErrors.includes('시작 일자와 종료 일자는 같아야 합니다.'),
                        },
                      }}
                      timeSteps={{ minutes: 10 }}
                      readOnly={isViewMode}
                    />
                    {validationErrors.includes('시작 시간은 현재 시각 이후여야 합니다.') && (
                      <Typography variant="caption" sx={{ color: 'error.main', display: 'block', mt: 0.5 }}>시작 시간은 현재 시각 이후여야 합니다.</Typography>
                    )}
                    {validationErrors.includes('시작 일자와 종료 일자는 같아야 합니다.') && (
                      <Typography variant="caption" sx={{ color: 'error.main', display: 'block', mt: 0.5 }}>시작 일자와 종료 일자는 같아야 합니다.</Typography>
                    )}
                    {validationErrors.includes('종료 시간은 시작 시간보다 이후여야 합니다.') && (
                      <Typography variant="caption" sx={{ color: 'error.main', display: 'block', mt: 0.5 }}>종료 시간은 시작 시간보다 이후여야 합니다.</Typography>
                    )}
                    {validationErrors.includes('예약 시간은 최소 10분 이상이어야 합니다.') && (
                      <Typography variant="caption" sx={{ color: 'error.main', display: 'block', mt: 0.5 }}>예약 시간은 최소 10분 이상이어야 합니다.</Typography>
                    )}
                  </Box>
                </Box>

                {/* 회의실 */}
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.75 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>회의실</Typography>
                    {currentRoom && (
                      <Tooltip title="회의실 정보">
                        <IconButton size="small" onClick={() => setIsRoomDetailModalOpen(true)}>
                          <InfoOutlinedIcon sx={{ fontSize: 14, color: 'primary.main' }} />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                  <Autocomplete
                    disablePortal
                    options={selectableRooms}
                    value={currentRoom}
                    openOnFocus
                    autoHighlight
                    filterOptions={(options, state) => {
                      const q = state.inputValue.trim().toLowerCase();
                      if (!q) return options;
                      return options.filter((r) => {
                        const haystack = [r.name, r.location, String(r.capacity ?? ''), typeof r.equipment === 'string' ? r.equipment : '', r.description ?? '']
                          .filter(Boolean).join(' ').toLowerCase();
                        return haystack.includes(q);
                      });
                    }}
                    onChange={(_, newRoom) => {
                      setFormData(prev => ({ ...prev, roomName: newRoom?.name ?? '' }));
                      setValidationErrors(prev => prev.filter(err => err !== '회의실을 선택하세요.'));
                    }}
                    getOptionLabel={(room) => room?.name ?? ''}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    disabled={isViewMode}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        hiddenLabel
                        placeholder={currentRoom ? '회의실 변경' : '회의실 선택'}
                        required
                        size="small"
                        error={validationErrors.includes('회의실을 선택하세요.')}
                        helperText={validationErrors.includes('회의실을 선택하세요.') ? '회의실을 선택하세요.' : ''}
                        InputProps={{ ...params.InputProps, endAdornment: params.InputProps.endAdornment }}
                        inputProps={{ ...params.inputProps, readOnly: true, style: { fontSize: 13 } }}
                      />
                    )}
                    renderOption={(props, room) => (
                      <li {...props} key={room.id}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, width: '100%' }}>
                          <Typography variant="body2">{room.name}</Typography>
                          {/* 기간이 걸린 회의실은 고르기 전에 언제까지인지 보이게 한다 */}
                          {hasClosureDate(room) && (
                            <Chip
                              label={formatClosureBadge(room)}
                              size="small"
                              color="warning"
                              variant="outlined"
                              sx={{ height: 18, fontSize: 11, '& .MuiChip-label': { px: 0.75 } }}
                            />
                          )}
                        </Box>
                      </li>
                    )}
                  />
                </Box>

              </Box>

              {/* ── 카드 2: 회의 정보 ── */}
              <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 1.5, display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>회의 정보</Typography>

                <TextField
                  placeholder="회의 제목을 입력하세요"
                  fullWidth
                  value={formData.title}
                  onChange={(e) => {
                    setFormData(prev => ({ ...prev, title: e.target.value }));
                    setValidationErrors(prev => prev.filter(err => err !== '회의 제목을 입력하세요.'));
                  }}
                  required
                  disabled={isViewMode}
                  size="small"
                  error={validationErrors.includes('회의 제목을 입력하세요.')}
                  helperText={validationErrors.includes('회의 제목을 입력하세요.') ? '회의 제목을 입력하세요.' : ' '}
                  inputProps={{ style: { fontSize: 13 } }}
                  InputProps={{
                    endAdornment: !isViewMode && formData.title ? (
                      <IconButton size="small" onClick={() => setFormData(prev => ({ ...prev, title: '' }))} edge="end" tabIndex={-1}>
                        <ClearIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    ) : null,
                  }}
                  FormHelperTextProps={{ sx: {
                    mt: 0, mb: 0,
                    ...(validationErrors.includes('회의 제목을 입력하세요.') ? {} : { fontSize: 0, lineHeight: 0 }),
                  } }}
                />

                <TextField
                  placeholder="회의 내용 (선택사항)"
                  fullWidth
                  multiline
                  rows={2}
                  size="small"
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  disabled={isViewMode}
                  inputProps={{ style: { fontSize: 13 } }}
                  InputProps={{
                    endAdornment: !isViewMode && formData.description ? (
                      <IconButton size="small" onClick={() => setFormData(prev => ({ ...prev, description: '' }))} edge="end" tabIndex={-1} sx={{ alignSelf: 'flex-start', mt: 0.25 }}>
                        <ClearIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    ) : null,
                  }}
                />
              </Box>

              {/* ── 카드 3: 참석자 ── */}
              <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 1.5 }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, display: 'block', mb: 1 }}>
                  참석자 <Typography component="span" variant="caption" color="text.secondary">(선택사항)</Typography>
                </Typography>
                <Autocomplete
                  multiple
                  id="attendees-autocomplete"
                  options={allUsers}
                  value={selectedAttendees}
                  onChange={(event, newValue) => { setSelectedAttendees(newValue); }}
                  sx={{ '& .MuiAutocomplete-clearIndicator svg': { fontSize: 14 } }}
                  getOptionLabel={(user) => getUserLabel(user)}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder={selectedAttendees.length === 0 ? "이름, 팀, 직함으로 검색" : ""}
                      disabled={isViewMode}
                      size="small"
                      inputProps={{ ...params.inputProps, style: { fontSize: 13 } }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          const input = e.target as HTMLInputElement;
                          const searchText = input.value.trim();
                          if (!searchText) return;
                          const filteredUsers = allUsers.filter(user =>
                            user?.name?.toLowerCase().includes(searchText.toLowerCase()) ||
                            user?.email?.toLowerCase().includes(searchText.toLowerCase()) ||
                            user?.team?.toLowerCase().includes(searchText.toLowerCase())
                          );
                          if (filteredUsers.length === 1) {
                            const option = filteredUsers[0];
                            if (option?.id && !selectedAttendees.some(a => a.id === option.id)) {
                              setSelectedAttendees([...selectedAttendees, option]);
                              setTimeout(() => {
                                input.value = '';
                                input.dispatchEvent(new Event('input', { bubbles: true }));
                              }, 0);
                              return;
                            }
                          }
                          if (params.inputProps['aria-activedescendant']) {
                            const activeOption = document.getElementById(params.inputProps['aria-activedescendant']);
                            if (activeOption) {
                              const optionIndex = parseInt(activeOption.getAttribute('data-option-index') || '0', 10);
                              const option = allUsers[optionIndex];
                              if (option?.id && !selectedAttendees.some(a => a.id === option.id)) {
                                setSelectedAttendees([...selectedAttendees, option]);
                                setTimeout(() => {
                                  input.value = '';
                                  input.dispatchEvent(new Event('input', { bubbles: true }));
                                }, 0);
                              }
                            }
                          }
                        }
                      }}
                    />
                  )}
                  renderOption={(props, user) => (
                    <li {...props} key={user.id}>
                      <Box>
                        <Typography variant="body2">{getUserLabel(user)}</Typography>
                        <Typography variant="caption" color="text.secondary">{user.email}</Typography>
                      </Box>
                    </li>
                  )}
                  disabled={isViewMode}
                  loading={isLoadingUsers}
                  renderValue={(value, getTagProps) =>
                    value.map((user, index) => {
                      const tagProps = getTagProps({ index });
                      return (
                        <Chip
                          label={getUserLabel(user)}
                          {...tagProps}
                          onDelete={isViewMode ? undefined : () => handleRemoveAttendee(user)}
                          disabled={isViewMode}
                          sx={{ '& .MuiChip-deleteIcon': { fontSize: 14 } }}
                        />
                      );
                    })
                  }
                />
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                  예약자 및 참석자 모두에게 예약 확정 메일이 발송됩니다.
                </Typography>
              </Box>

              {/* ── 카드 4: 알림 설정 (생성/수정 모드만) ── */}
              {!isViewMode && (
                <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 1.5, display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>알림 설정</Typography>

                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                    {/* 리마인드 알림 */}
                    <Box sx={{ flex: '1 1 220px' }}>
                      <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.75 }}>
                        리마인드 알림
                        <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5, visibility: formData.is_reminder_enabled ? 'visible' : 'hidden' }}>
                          (회의 {formData.reminder_minutes}분 전에 알림)
                        </Typography>
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                        {[{ label: '알림 안함', value: 0 }, { label: '5분', value: 5 }, { label: '10분', value: 10 }, { label: '15분', value: 15 }, { label: '20분', value: 20 }, { label: '30분', value: 30 }].map(({ label, value }) => {
                          const selected = value === 0 ? !formData.is_reminder_enabled : formData.is_reminder_enabled && formData.reminder_minutes === value;
                          return (
                            <Chip
                              key={value}
                              label={label}
                              clickable
                              size="small"
                              onClick={() => setFormData(prev => ({
                                ...prev,
                                is_reminder_enabled: value !== 0,
                                reminder_minutes: value !== 0 ? value : prev.reminder_minutes,
                              }))}
                              variant={selected ? 'filled' : 'outlined'}
                              color={selected ? 'primary' : 'default'}
                            />
                          );
                        })}
                      </Box>
                    </Box>

                    <Box sx={{ width: '1px', bgcolor: 'divider', alignSelf: 'stretch', opacity: 0.6, display: { xs: 'none', sm: 'block' }, flexShrink: 0 }} />

                    {/* 예약 반복 설정 */}
                    <Box sx={{ flex: '1 1 180px' }}>
                      <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.75 }}>
                        예약 반복 설정
                        {(!editingBooking && formData.show_recurrence_notice) && (
                          <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                            (반복 예약은 최대 3개월까지 설정 가능합니다.)
                          </Typography>
                        )}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                        {['반복 안함', '매주 반복', '격주 반복', '매월 반복'].map((option) => (
                          <Chip
                            key={option}
                            label={option}
                            clickable={!Boolean(editingBooking)}
                            size="small"
                            onClick={() => {
                              if (Boolean(editingBooking)) return;
                              setFormData(prev => ({
                                ...prev,
                                recurrence_type: option,
                                show_recurrence_notice: option !== '반복 안함',
                              }));
                            }}
                            variant={formData.recurrence_type === option ? 'filled' : 'outlined'}
                            color={formData.recurrence_type === option ? 'primary' : 'default'}
                            disabled={Boolean(editingBooking)}
                          />
                        ))}
                      </Box>
                      {editingBooking && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                          반복 설정은 수정할 수 없습니다. 삭제 후 다시 예약해주세요.
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Box>
              )}

            </Box>
          )}

          {/*
            각 입력칸 옆에 직접 표시되지 않는 오류를 모아 보여준다.
            이 안내가 없으면 예약 버튼을 눌러도 아무 반응이 없는 것처럼 보인다.
          */}
          {generalErrors.length > 0 && (
            <Alert severity="error" sx={{ mt: 1.5 }}>
              {generalErrors.map(message => (
                <Typography key={message} variant="body2">
                  {message}
                </Typography>
              ))}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Box sx={{ flexGrow: 1 }} />
          {editingBooking && isOwner && canEndEarly && isLoggedIn && (
            <Button onClick={handleEndMeeting} color="warning">
              회의 종료
            </Button>
          )}
          {editingBooking && isOwner && onDelete && isLoggedIn && !(isInProgress && canEndEarly) && (
            <Button onClick={handleDeleteClick} color="error">
              {isPastBooking || (isInProgress && !canEndEarly) ? '삭제' : '예약 취소'}
            </Button>
          )}
          {!isViewMode && isLoggedIn && (
            <Button onClick={handleSubmit} color="primary" disabled={isSubmitting}>
              {editingBooking ? '수정' : '예약'}
            </Button>
          )}
          <Button onClick={handleClose} color="inherit">
            닫기
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* 반복 예약 삭제 옵션 다이얼로그 */}
      <Dialog 
        open={deleteDialogOpen} 
        onClose={handleDeleteDialogClose}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            padding: 1
          }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          반복 일정 삭제
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Box sx={{ p: 1 }}>
            <FormControl component="fieldset">
              <RadioGroup
                value={recurrenceDeleteType}
                onChange={(e) => setRecurrenceDeleteType(e.target.value as RecurrenceDeleteType)}
              >
                <FormControlLabel 
                  value="THIS_EVENT" 
                  control={<Radio color="primary" />} 
                  label="이 일정" 
                />
                <FormControlLabel 
                  value="THIS_AND_FOLLOWING" 
                  control={<Radio color="primary" />} 
                  label="이 일정 및 향후 일정" 
                />
                <FormControlLabel 
                  value="ALL_EVENTS" 
                  control={<Radio color="primary" />} 
                  label="모든 일정" 
                />
              </RadioGroup>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteDialogClose} color="inherit">
            취소
          </Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            삭제
          </Button>
        </DialogActions>
      </Dialog>

      {/* 겹치는 예약 안내 모달 */}
      <MuiDialog open={showConflictDialog} onClose={handleCancelConflict} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ color: '#222'}}>
          {pendingConflicts.length === 1
            ? '예약 진행 시 1개 일정이 중복됩니다.'
            : `예약 진행 시 ${pendingConflicts.length}개 이상 일정이 중복됩니다.`}
        </DialogTitle>
        <DialogContent sx={{ bgcolor: '#fff' }}>
          <Box sx={{ bgcolor: '#fff8ee', borderRadius: 2, p: 2, mb: 0, border: '1px solid', borderColor: 'warning.light', display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ color: '#222', fontWeight: 600, mb: 1, fontSize: 15, display: 'flex', alignItems: 'center', gap: 0.5}}>
                <WarningAmberIcon sx={{ fontSize: 18, color: 'warning.main', mr: 0.5 }} />
                {pendingConflicts.length === 1
                  ? '충돌 예약 정보 (1개):'
                  : `충돌 예약 정보 (${pendingConflicts.length}개 이상):`}
              </Typography>
              {pendingConflicts && pendingConflicts.length > 0 && (
                <Typography variant="body2" sx={{ color: '#222', fontSize: 14, whiteSpace: 'pre-line', mb: 0 }}>
                  회의 날짜: {formatConflictDate(pendingConflicts[0].startTime)}
                  {'\n'}회의 장소: {pendingConflicts[0].roomName}
                  {'\n'}회의 제목: {pendingConflicts[0].title}
                </Typography>
              )}
            </Box>
          </Box>
          <Typography sx={{ mt: 2, mb: 0.5, color: '#222', fontSize: 14 }}>
            중복된 시간에는 예약이 생성되지 않습니다.
          </Typography>
          <Typography sx={{ mb: 0, color: '#222', fontSize: 14 }}>
            중복된 예약을 제외하고 나머지 반복 예약을 진행하시겠습니까?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ bgcolor: '#fff' }}>
          <Button onClick={handleCancelConflict} color="inherit">취소</Button>
          <Button onClick={handleConfirmConflict} color="primary" variant="contained" disabled={isSubmitting}>계속 진행하기</Button>
        </DialogActions>
      </MuiDialog>

      <RoomDetailModal
        open={isRoomDetailModalOpen}
        onClose={() => setIsRoomDetailModalOpen(false)}
        room={currentRoom}
      />
    </LocalizationProvider>
    </ThemeProvider>
  );
};

export default BookingModal; 

// 유틸: 겹치는 예약 날짜 포맷 함수 추가
function formatConflictDate(date: Date | string) {
  const d = typeof date === 'string' ? new Date(date) : date;
  return d.toLocaleString('ko-KR', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    weekday: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  });
} 
